import type { IntlShape } from 'react-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { fg } from '@confluence/feature-gating';
import { token } from '@atlaskit/tokens';
import { i18n } from '../confluenceDatabasesExtensionI18n.ts';
import { useInitialUrl } from '../hooks/useInitialUrl.ts';
import DatabaseReferenceModalCurrentUserQuery, {
	type DatabaseReferenceModalCurrentUserQuery$data,
} from '../__generated__/DatabaseReferenceModalCurrentUserQuery.graphql.ts';
import type {
	DatabaseReferenceModalCurrentUserQuery_user_emails,
	DatabaseReferenceModalCurrentUserQuery_user_photos,
} from '../__types__/DatabaseReferenceModalCurrentUserQuery.ts';

import { DatabaseReferenceModalSkeleton } from './DatabaseReferenceModalSkeleton.tsx';

import {
	queryInterop,
	type QueryInteropSupportedResult,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/query.ts';

export type DatabaseReferenceModalProps = {
	onClose: (result: string | undefined) => void;
	intl: IntlShape;
};

type NullableString = string | null | undefined;

type Identity = {
	accountId: NullableString;
	nickname: NullableString;
	picture: NullableString;
	email: NullableString;
	locale: NullableString;
};

const isDatabaseUrl = (value: string): boolean => /database\/\d+/.test(value);

export const DatabaseReferenceModal = ({
	onClose,
	intl,
}: DatabaseReferenceModalProps): React.JSX.Element => {
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const [identity, setIdentity] = useState<Identity | null>(null);
	const { activationId, userId, cloudId } = useSessionData();
	const initialUrl = useInitialUrl();
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		void queryInterop<DatabaseReferenceModalCurrentUserQuery$data>({
			query: DatabaseReferenceModalCurrentUserQuery,
		}).then(
			({ data }: QueryInteropSupportedResult<DatabaseReferenceModalCurrentUserQuery$data>) => {
				setIdentity({
					accountId: data?.user?.id,
					nickname: data?.user?.displayName,
					picture: data?.user?.photos?.find(
						(photo: DatabaseReferenceModalCurrentUserQuery_user_photos | null | undefined) =>
							photo?.isPrimary,
					)?.value,
					email: data?.user?.emails?.find(
						(email: DatabaseReferenceModalCurrentUserQuery_user_emails | null | undefined) =>
							email?.isPrimary,
					)?.value,
					locale: data?.user?.locale,
				});
			},
		);
	}, []);

	const bridgeData = {
		cloudId,
		activationId,
		userId,
		identity,
	};

	const messageCallback = useCallback(
		(event: MessageEvent) => {
			const isFromExpectedIframe =
				iframeRef.current && event.source === iframeRef.current.contentWindow;

			if (isFromExpectedIframe) {
				try {
					const data = JSON.parse(event.data);
					if (data.finalDatabaseUrl && isDatabaseUrl(data.finalDatabaseUrl)) {
						onClose(data.finalDatabaseUrl);
					} else if (data.cancelDatabaseReference) {
						onClose(undefined);
					} else if (data.databaseReferencingComponentLoaded) {
						setIsLoading(false);
					}
				} catch {}
			}
		},
		[onClose, iframeRef],
	);

	useEffect(() => {
		window.addEventListener('message', messageCallback);

		return () => {
			window.removeEventListener('message', messageCallback);
		};
	}, [messageCallback]);

	return (
		<ModalTransition>
			{/* eslint-disable-next-line @atlaskit/design-system/no-modal-label */}
			<Modal
				width={1200}
				height={592}
				onClose={() => onClose(undefined)}
				testId="reference-database-modal"
				label={intl.formatMessage(i18n.referenceDatabaseModalLabel)}
			>
				<ModalHeader hasCloseButton>
					<ModalTitle>{intl.formatMessage(i18n.referenceDatabaseTitle)}</ModalTitle>
				</ModalHeader>

				{isLoading || !identity ? <DatabaseReferenceModalSkeleton /> : null}
				<iframe
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						width: '100%',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						height: '505px',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop, confluence-feature-gating/no-new-feature-gates -- Ignored via go/DSP-18766; Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
						border: fg('confluence_frontend_tokenize_border') ? token('space.0') : '0px',
						display: !isLoading && identity ? 'block' : 'none',
					}}
					src={initialUrl}
					{...(identity ? { 'data-bridge-data': JSON.stringify(bridgeData) } : {})}
					title="reference-database-frame"
					ref={iframeRef}
				/>
			</Modal>
		</ModalTransition>
	);
};
